<template>
  <aside
    class="state-rules"
    role="dialog"
    :aria-label="$t('communication-status-panel.legend-title')"
  >
    <header class="state-rules__header">
      <h2 class="state-rules__title">{{ $t('communication-status-panel.legend-title') }}</h2>
      <button
        type="button"
        class="state-rules__close"
        :aria-label="$t('communication-status-panel.close')"
        @click="$emit('close')"
      >
        <s-icon name="close" />
      </button>
    </header>

    <p class="state-rules__intro">{{ $t('communication-status-panel.legend-intro') }}</p>

    <ul class="state-rules__list">
      <li v-for="rule in rules" :key="rule.state" class="state-rules__item">
        <span class="state-rules__swatch" :class="`state-rules__swatch--${rule.state}`" />
        <div class="state-rules__text">
          <strong class="state-rules__name">{{ rule.label }}</strong>
          <span class="state-rules__rule">{{ rule.description }}</span>
        </div>
      </li>
    </ul>

    <div class="state-rules__neutral">
      <strong>{{ $t('communication-status-panel.legend-neutral-title') }}</strong>
      <p>{{ $t('communication-status-panel.legend-neutral-detail') }}</p>
      <ul class="state-rules__neutral-list">
        <li v-for="status in neutralStatuses" :key="status">
          {{ $t(`enums.module-status.${status}`) }}
        </li>
      </ul>
    </div>

    <div class="state-rules__sensors">
      <strong>{{ $t('communication-status-panel.legend') }}</strong>
      <ul class="state-rules__sensor-list">
        <li
          v-for="sensor in sensorStatuses"
          :key="sensor.status"
          class="state-rules__sensor"
          :class="{ 'state-rules__sensor--neutral': sensor.neutral }"
        >
          <span
            class="state-rules__sensor-swatch"
            :class="`state-rules__sensor-swatch--${sensor.status}`"
          />
          {{ sensor.label }}
        </li>
      </ul>
    </div>
  </aside>
</template>

<script>
import { assetStatesBySeverity } from '@/domain/enums/assetState';
import { moduleStatus } from '@/domain/enums/module';
import {
  NEUTRAL_STATUSES,
  isNeutralStatus,
} from '@/domain/services/generalPanel/communicationStatusPanel/panelAggregator';

// Agrupados pelo Estado Macro que produzem — Atenção, Normal, Fora de Operação —
// para a legenda ler como a tabela de regras acima dela.
const SENSOR_LEGEND_ORDER = [
  moduleStatus.INACTIVE,
  moduleStatus.INDETERMINATE,
  moduleStatus.ESTABLISHING_COMMUNICATION,
  moduleStatus.PARTIAL_COMMUNICATION,
  moduleStatus.ACTIVE,
  moduleStatus.SIMULATION,
  moduleStatus.DISABLED,
  moduleStatus.UNDER_MAINTENANCE,
];

export default {
  name: 'StateRulesLegend',
  computed: {
    /** Cada estado com a regra que o produz, na ordem de severidade. */
    rules() {
      return assetStatesBySeverity.map(state => ({
        state,
        label: this.$t(`communication-status-panel.state.${state}`),
        description: this.$t(`communication-status-panel.state-rule.${state}`),
      }));
    },
    neutralStatuses() {
      return NEUTRAL_STATUSES;
    },
    /** As oito cores de sensor, antes exibidas numa faixa fixa no rodapé. */
    sensorStatuses() {
      return SENSOR_LEGEND_ORDER.map(status => ({
        status,
        neutral: isNeutralStatus(status),
        label: this.$t(`enums.module-status.${status}`),
      }));
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.state-rules
  background: var(--panel-surface-raised)
  border: 1px solid var(--panel-border)
  border-radius: 6px
  box-shadow: 0 12px 32px var(--panel-popover-shadow)
  color: var(--panel-text)
  max-width: 30rem
  padding: 0.9rem 1rem
  position: absolute
  right: 1.2rem
  top: 100%
  z-index: 20

  &__header
    align-items: center
    display: flex
    justify-content: space-between

  &__title
    font-size: 0.95rem
    font-weight: 700
    letter-spacing: 0.03em
    margin: 0
    text-transform: uppercase

  &__close
    background: transparent
    border: none
    color: var(--panel-text-muted)
    cursor: pointer
    font-size: 0.9rem
    line-height: 1
    padding: 0.2rem

    &:hover
      color: var(--panel-text)

  &__intro
    color: var(--panel-text-muted)
    font-size: 0.78rem
    margin: 0.35rem 0 0.7rem

  &__list
    display: flex
    flex-direction: column
    gap: 0.55rem
    list-style: none
    margin: 0
    padding: 0

  &__item
    display: flex
    gap: 0.6rem

  &__swatch
    border-radius: 3px
    flex: none
    height: 1.1rem
    margin-top: 0.1rem
    width: 0.35rem

    &--attention
      background: var(--panel-attention)
    &--normal
      background: var(--panel-normal)
    &--out-of-service
      background: var(--panel-out-of-service)

  &__text
    display: flex
    flex-direction: column

  &__name
    font-size: 0.82rem

  &__rule
    color: var(--panel-text-muted)
    font-size: 0.76rem
    line-height: 1.35

  &__neutral
    border-top: 1px solid var(--panel-border)
    margin-top: 0.8rem
    padding-top: 0.7rem

    strong
      font-size: 0.82rem

    p
      color: var(--panel-text-muted)
      font-size: 0.76rem
      line-height: 1.35
      margin: 0.2rem 0 0.4rem

  &__neutral-list
    display: flex
    flex-wrap: wrap
    gap: 0.3rem
    list-style: none
    margin: 0
    padding: 0

    li
      background: var(--panel-sensor-disabled)
      border-radius: 999px
      color: var(--panel-text)
      font-size: 0.7rem
      padding: 0.15rem 0.55rem

  &__sensors
    border-top: 1px solid var(--panel-border)
    margin-top: 0.8rem
    padding-top: 0.7rem

    strong
      font-size: 0.82rem

  &__sensor-list
    display: grid
    gap: 0.3rem 0.7rem
    grid-template-columns: repeat(2, minmax(0, 1fr))
    list-style: none
    margin: 0.4rem 0 0
    padding: 0

  &__sensor
    align-items: center
    color: var(--panel-text)
    display: flex
    font-size: 0.74rem
    gap: 0.35rem

    &--neutral
      color: var(--panel-text-muted)

  &__sensor-swatch
    border-radius: 2px
    flex: none
    height: 0.65rem
    width: 1rem

    &--active
      background: var(--panel-sensor-active)
    &--inactive
      background: var(--panel-sensor-inactive)
    &--partial-communication
      background: var(--panel-sensor-partial-communication)
    &--establishing-communication
      background: var(--panel-sensor-establishing-communication)
    &--indeterminate
      background: var(--panel-sensor-indeterminate)
    &--disabled
      background: var(--panel-sensor-disabled)
    &--under-maintenance
      background: var(--panel-sensor-under-maintenance)
    &--simulation
      background: var(--panel-sensor-simulation)

  // Celular: o popover ocupa a largura, com margem, em vez de 30rem ancorados à
  // direita que sairiam da tela pela esquerda.
  +media-max-sm()
    left: 0.5rem
    max-width: none
    right: 0.5rem

    &__sensor-list
      grid-template-columns: minmax(0, 1fr)
</style>
